import { getFuelColor } from '../../../../utils/fuelColors';
import { hasExpandableVoSubfuels } from '../generationProfileInputsUtils';
import { voFlexSubfuelColor, VO_FLEX_CHART_FUELS } from './hourlyChartColors';
import { buildHourlyTimeSeriesMonthSlice } from '../subComponents/hourlyChartMonthWindow';

/**
 * Build Highcharts series array for the hourly generation/demand stacked chart.
 * Separated from options shell so compact month navigation only recomputes data slices.
 */
export function buildHourlyChartSeries({
  hourlyProfile,
  simulation,
  solarProfile,
  windProfile,
  windOffProfile,
  nuclearProfile,
  hydroProfile,
  year,
  visibleMonthIndex,
  useMonthlyWindow,
  voFlexFuelCollapsed,
  legendVisibility = {},
}) {
  if (!hourlyProfile || !simulation) {
    return null;
  }

  const yearStartMs = Date.UTC(year, 0, 1);
  const mwToGw = (value) => value / 1000;

  const createTimeSeries = (profileData) => {
    if (!profileData || !Array.isArray(profileData)) return null;
    if (useMonthlyWindow) {
      return buildHourlyTimeSeriesMonthSlice(profileData, year, visibleMonthIndex, mwToGw);
    }
    return profileData.map((value, index) => [
      yearStartMs + index * 3600000,
      mwToGw(value),
    ]);
  };

  const totalTimeData = createTimeSeries(hourlyProfile);
  const solarTimeData = createTimeSeries(solarProfile);
  const windTimeData = createTimeSeries(windProfile);
  const windOffTimeData = createTimeSeries(windOffProfile);
  const nuclearTimeData = createTimeSeries(nuclearProfile);
  const hydroArr = simulation.hydroSim || hydroProfile;
  const hydroTimeData = createTimeSeries(hydroArr);

  const simTimeSeries = (arr) => {
    if (!arr) return null;
    if (useMonthlyWindow) {
      return buildHourlyTimeSeriesMonthSlice(arr, year, visibleMonthIndex, mwToGw);
    }
    return arr.map((value, index) => [yearStartMs + index * 3600000, mwToGw(value)]);
  };

  const bioMustRunData = simTimeSeries(simulation.bioMustRun);
  const coalMustRunData = simTimeSeries(simulation.coalMustRun);
  const geoMustRunData = simTimeSeries(simulation.geoMustRun);
  const bioFlexData = simTimeSeries(simulation.bioFlex);
  const coalFlexData = simTimeSeries(simulation.coalFlex);
  const gasFlexData = simTimeSeries(simulation.gasFlex);
  const oilFlexData = simTimeSeries(simulation.oilFlex);
  const stDischargeData = simTimeSeries(simulation.storageDischarge);
  const pumpedDischargeData = simTimeSeries(simulation.pumpedStorageDischarge);

  const stChargeData = useMonthlyWindow
    ? buildHourlyTimeSeriesMonthSlice(
        simulation.storageCharge,
        year,
        visibleMonthIndex,
        (value) => -value / 1000
      )
    : simulation.storageCharge.map((value, index) => [
        yearStartMs + index * 3600000,
        -value / 1000,
      ]);

  const pumpedChargeData = useMonthlyWindow
    ? buildHourlyTimeSeriesMonthSlice(
        simulation.pumpedStorageCharge,
        year,
        visibleMonthIndex,
        (value) => -value / 1000
      )
    : simulation.pumpedStorageCharge.map((value, index) => [
        yearStartMs + index * 3600000,
        -value / 1000,
      ]);

  const series = [];
  let zIdx = 1;

  const getSavedVisibility = (id, name) => {
    const saved = legendVisibility;
    if (Object.prototype.hasOwnProperty.call(saved, id)) return saved[id];
    if (Object.prototype.hasOwnProperty.call(saved, name)) return saved[name];
    return undefined;
  };

  const addStackSeries = (id, name, data, colorKey) => {
    if (data && data.length > 0 && data.some((d) => d[1] !== 0)) {
      const savedVisible = getSavedVisibility(id, name);
      series.push({
        id,
        name,
        data,
        type: 'column',
        color: getFuelColor(colorKey) || '#7F8C8D',
        stacking: 'normal',
        borderWidth: 0,
        zIndex: zIdx++,
        visible: savedVisible,
        dataGrouping: { enabled: false },
      });
    }
  };

  addStackSeries('hydro', 'Hydro', hydroTimeData, 'hydro');
  addStackSeries('solar', 'Solar', solarTimeData, 'solar');
  addStackSeries('windon', 'WindOn', windTimeData, 'windon');
  addStackSeries('windoff', 'WindOff', windOffTimeData, 'windoff');

  if (pumpedDischargeData && pumpedDischargeData.some((d) => d[1] !== 0)) {
    const pumpedColor = getFuelColor('pumped_storage');
    const savedVisible = getSavedVisibility('pumped_storage_discharge', 'Pumped Discharge');
    series.push({
      id: 'pumped_storage_discharge',
      name: 'Pumped Discharge',
      data: pumpedDischargeData,
      type: 'column',
      color: pumpedColor !== '#7F8C8D' ? pumpedColor : '#0080ff',
      stacking: 'normal',
      borderWidth: 0,
      zIndex: zIdx++,
      visible: savedVisible,
      dataGrouping: { enabled: false },
    });
  }

  if (stDischargeData && stDischargeData.some((d) => d[1] !== 0)) {
    const savedVisible = getSavedVisibility('storage_discharge', 'Battery Discharge');
    series.push({
      id: 'storage_discharge',
      name: 'Battery Discharge',
      data: stDischargeData,
      type: 'column',
      color: getFuelColor('storage') !== '#7F8C8D' ? getFuelColor('storage') : '#9B59B6',
      stacking: 'normal',
      borderWidth: 0,
      zIndex: zIdx++,
      visible: savedVisible,
      dataGrouping: { enabled: false },
    });
  }

  const parentFlexData = {
    bio: bioFlexData,
    coal: coalFlexData,
    gas: gasFlexData,
    oil: oilFlexData,
  };
  const flexByFuel = { bio: [], coal: [], gas: [], oil: [] };
  if (
    simulation.dispatchMode === 'vo-report-tech' &&
    Array.isArray(simulation.flexDispatchOrder)
  ) {
    for (const row of simulation.flexDispatchOrder) {
      if (!row?.fuel || !flexByFuel[row.fuel]) continue;
      if (!row.hourlyMW || !row.hourlyMW.length) continue;
      flexByFuel[row.fuel].push(row);
    }
  }

  for (const def of VO_FLEX_CHART_FUELS) {
    const kids = flexByFuel[def.fuel] || [];
    const showSubtechs =
      hasExpandableVoSubfuels(kids) && voFlexFuelCollapsed[def.fuel] === false;
    if (showSubtechs) {
      const baseColor = getFuelColor(def.colorKey) || '#7F8C8D';
      kids.forEach((row) => {
        const data = simTimeSeries(Array.from(row.hourlyMW));
        if (!data || !data.some((d) => d[1] !== 0)) return;
        const id = `flex_${row.tech}|${row.fuel}|${row.techByFuel ?? ''}`;
        const name = row.label || row.tech;
        const savedVisible = getSavedVisibility(id, name);
        series.push({
          id,
          name,
          data,
          type: 'column',
          color: voFlexSubfuelColor(baseColor, def.fuel, row.techId),
          stacking: 'normal',
          borderWidth: 0,
          zIndex: zIdx++,
          visible: savedVisible,
          dataGrouping: { enabled: false },
          custom: { voFlexFuel: def.fuel },
        });
      });
    } else {
      addStackSeries(def.parentId, def.parentName, parentFlexData[def.fuel], def.colorKey);
    }
  }

  addStackSeries('geo_mustrun', 'Geo (must-run)', geoMustRunData, 'geothermal');
  addStackSeries('bio_mustrun', 'Bio (must-run)', bioMustRunData, 'bioenergy');
  addStackSeries('coal_mustrun', 'Coal (must-run)', coalMustRunData, 'coal');
  addStackSeries('nuclear', 'Nuclear', nuclearTimeData, 'nuclear');

  if (stChargeData && stChargeData.some((d) => d[1] !== 0)) {
    const savedVisible = getSavedVisibility('storage_charge', 'Battery Charge');
    series.push({
      id: 'storage_charge',
      name: 'Battery Charge',
      data: stChargeData,
      type: 'column',
      color: '#D8BFD8',
      stacking: 'normal',
      borderWidth: 0,
      zIndex: zIdx++,
      visible: savedVisible,
      dataGrouping: { enabled: false },
    });
  }

  if (pumpedChargeData && pumpedChargeData.some((d) => d[1] !== 0)) {
    const savedVisible = getSavedVisibility('pumped_storage_charge', 'Pumped Charge');
    series.push({
      id: 'pumped_storage_charge',
      name: 'Pumped Charge',
      data: pumpedChargeData,
      type: 'column',
      color: '#5DADE2',
      stacking: 'normal',
      borderWidth: 0,
      zIndex: zIdx++,
      visible: savedVisible,
      dataGrouping: { enabled: false },
    });
  }

  if (totalTimeData) {
    const savedVisible = getSavedVisibility('demand', 'Demand');
    series.push({
      id: 'demand',
      name: 'Demand',
      data: totalTimeData,
      type: 'line',
      color: '#6366F1',
      lineWidth: 2.5,
      marker: { enabled: false },
      zIndex: zIdx + 5,
      visible: savedVisible,
      dataGrouping: { enabled: false },
    });
  }

  return series.length > 0 ? series : null;
}
